'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { Carousel } from './ui/carousel';

// Define the HoverCard interface with an explicit id
interface HoverCard {
  id: string;
  backgroundImage: string;
  productImage: string;
  title: string;
  description: string;
  features: Array<{
    icon: string;
    text: string;
  }>;
  ctaText: string;
  link: string;
}

const hoverCards: HoverCard[] = [
  {
    id: '1',
backgroundImage: '/images/home/hover-card-1.webp',
productImage: '/images/product/battery/E3600LFP.webp',
    title: 'E3600LFP 3600W/3072Wh',
    description: 'Expandable Backup Power',
    features: [
      { icon: '/images/home/icons/charge-icon.svg', text: '3072Wh/3600W' },
      { icon: '/images/home/icons/battery-icon.svg', text: '1.3hrs Fast AC Charging' },
      { icon: '/images/home/icons/phone-icon.svg', text: 'Smart App Control' },
      { icon: '/images/home/icons/expand-icon.svg', text: 'Expand up to 30720Wh' },
    ],
    ctaText: 'Buy Now',
    link: '/products/e3600lfp',
  },
  {
    id: '2',
backgroundImage: '/images/home/hover-card-2.webp',
productImage: '/images/product/battery/E600LFP.webp',
    title: 'E600LFP 1200W/614Wh',
    description: 'Power Your Outdoor Adventure',
    features: [
      { icon: '/images/home/icons/charge-icon.svg', text: '614Wh/1200W' },
      { icon: '/images/home/icons/battery-icon.svg', text: '300W Fast Charging' },
      { icon: '/images/home/icons/phone-icon.svg', text: 'LiFePO4 Battery' },
      { icon: '/images/home/icons/wireless-icon.svg', text: 'Wireless Charging' },
    ],
    ctaText: 'Buy Now',
    link: '/products/e600lfp',
  },
  {
    id: '3',
backgroundImage: '/images/home/hover-card-3.webp',
productImage: '/images/product/battery/P302.webp',
    title: 'P302 300W/296Wh',
    description: 'Power Your Weekend Getaway',
    features: [
      { icon: '/images/home/icons/charge-icon.svg', text: '296Wh/300W' },
      { icon: '/images/home/icons/battery-icon.svg', text: 'AC, Solar & Car charging' },
      { icon: '/images/home/icons/phone-icon.svg', text: '4 Light Modes' },
      { icon: '/images/home/icons/expand-icon.svg', text: '13 Outputs' },
    ],
    ctaText: 'Buy Now',
    link: '/products/p302',
  },
  {
    id: '4',
backgroundImage: '/images/home/hover-card-4.webp',
productImage: '/images/product/battery/P201.webp',
    title: 'P201 150W/148Wh',
    description: 'Small But Mighty',
    features: [
      { icon: '/images/home/icons/charge-icon.svg', text: '148Wh/150W' },
      { icon: '/images/home/icons/battery-icon.svg', text: 'Only 1.8kg' },
      { icon: '/images/home/icons/phone-icon.svg', text: '3 Light Modes' },
      { icon: '/images/home/icons/expand-icon.svg', text: '9 Outputs' },
    ],
    ctaText: 'Buy Now',
    link: '/products/p201',
  },
];

// Extend props with id and make className optional with a default
interface HoverCardProps extends HoverCard {
  className?: string;
}

function HoverCard({
  id,
  backgroundImage,
  productImage,
  title,
  description,
  features,
  ctaText,
  link,
  className = '',
}: HoverCardProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const handleCardClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      setIsMobileOpen(prev => !prev);
    }
  }, []);

  return (
    <div
      onClick={handleCardClick}
      className={`group relative h-[300px] sm:h-[400px] md:h-[473px] lg:h-[500px] xl:h-[550px] w-[280px] sm:w-[450px] min-w-[280px] sm:min-w-[450px] overflow-hidden rounded-md ${className}`}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image src={backgroundImage} alt={`${title} background`} fill className="object-cover" priority />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
      </div>

      {/* Unhovered State - Text overlay */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-4 sm:p-6 text-white transition-opacity duration-300 group-hover:opacity-0">
        <div className="space-y-1">
          <h3 className="text-xl sm:text-2xl leading-7 font-bold">{title}</h3>
          <p className="text-sm sm:text-base leading-5">{description}</p>
        </div>
      </div>

      {/* Hovered State - Detailed product card */}
      <div className={`absolute inset-0 z-20 flex flex-col transform transition-transform duration-300 ease-in-out ${isMobileOpen ? 'translate-y-0' : 'translate-y-full'} sm:group-hover:translate-y-0 bg-white rounded-md overflow-hidden`}>
        {/* Product Image Section */}
        <div className="flex-shrink-0 h-[150px] sm:h-[250px] hidden sm:flex items-center justify-center bg-gray-100 px-4 sm:px-20 py-5">
          <div className="relative h-[120px] w-[180px] sm:h-[207px] sm:w-[298px]">
            <Image src={productImage} alt={title} fill className="object-contain" />
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-grow p-4 sm:p-6 space-y-4 overflow-y-auto">
          <div className="space-y-1">
            <h3 className="text-xl sm:text-2xl leading-7 font-bold text-gray-900">{title}</h3>
            <p className="text-sm sm:text-base leading-tight text-gray-600">{description}</p>
          </div>

          {/* Features Grid */}
          <div className="space-y-4 sm:space-y-7">
            <div className="flex gap-4 sm:gap-7">
              {features.slice(0, 2).map((feature, index) => (
                <div key={`${id}-feature-${index}`} className="flex flex-1 items-center gap-2">
                  <div className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0">
                    <Image
                      src={feature.icon}
                      alt={feature.text}
                      width={24}
                      height={24}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <span className="text-xs sm:text-sm font-normal text-black">{feature.text}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 sm:gap-7">
              {features.slice(2, 4).map((feature, index) => (
                <div key={`${id}-feature-${index + 2}`} className="flex flex-1 items-center gap-2">
                  <div className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0">
                    <Image
                      src={feature.icon}
                      alt={feature.text}
                      width={24}
                      height={24}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <span className="text-xs sm:text-sm font-normal text-black">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="pt-2">
            <Link
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded border border-gray-300 bg-white px-3 sm:px-4 py-1 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-50"
            >
              {ctaText}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HomepageHoverCards() {
  const carouselItems = hoverCards.map((card) => (
    <HoverCard
      key={card.id}
      id={card.id}
      backgroundImage={card.backgroundImage}
      productImage={card.productImage}
      title={card.title}
      description={card.description}
      features={card.features}
      ctaText={card.ctaText}
      link={card.link}
      className="flex-shrink-0"
    />
  ));

  return (
    <section className="w-full py-0">
      <div className="w-full">
        <Carousel
          items={carouselItems}
          config={{
            speed: 0.5,
            hoverSpeed: 0.2,
            gap: 16,
            showGradients: true,
            autoScroll: true,
            pauseOnHover: true,
            showNavigation: true,
            navigationClasses: {
              wrapper: 'mt-6 mr-4 lg:mr-15 pr-4',
              navigation: 'gap-3',
              buttonPrev: 'w-12 h-12 bg-gray-100 border-2 border-transparent hover:border-cyan-400 rounded-full transition-all',
              buttonNext: 'w-12 h-12 bg-gray-100 border-2 border-transparent hover:border-cyan-400 rounded-full transition-all',
              buttonIcon: 'w-6 h-6 text-gray-600',
            },
          }}
          className="py-0"
        />
      </div>
    </section>
  );
}